<template>
    <v-row>
        <v-col sm="8">
            <v-card flat>
                <v-card-text v-if="feedback">
                    <v-list outlined>
                        <v-list-item class="mb-2">
                            <v-list-item-avatar size="28" class="me-3" color="primary">
                                <v-icon size="18" color="white">{{ icons.mdiAccountOutline }}</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-subtitle>
                                    <template>
                                        {{ $t('Sinä') }}:
                                    </template>
                                    {{ getDateTime(feedback.insert_date) }}
                                    <v-divider class="my-1"></v-divider>
                                </v-list-item-subtitle>
                                <v-list-item-title v-if="feedback.feedback" class="pt-3">
                                    {{ feedback.feedback }}
                                </v-list-item-title>

                                <div v-if="feedback.form_submission" class="pt-3">
                                    <FormSubmissionRenderer :responses="feedback.form_submission.responses" />
                                </div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="feedback.attachment">
                            <v-list-item-icon>
                                <v-icon>{{ icons.mdiAttachment }}</v-icon>
                            </v-list-item-icon>
                            <v-list-item-subtitle>
                                {{ feedback.attachment_name }}
                            </v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item v-for="attachment in feedback.attachments" :key="attachment.id">
                            <v-list-item-icon>
                                <v-icon>{{ icons.mdiAttachment }}</v-icon>
                            </v-list-item-icon>
                            <v-list-item-subtitle>
                                {{ attachment.name }}
                            </v-list-item-subtitle>
                        </v-list-item>
                    </v-list>

                    <v-list>
                        <v-list-item v-for="message in feedback.messages" class="mb-6" :key="message.id">
                            <v-list-item-avatar size="28" class="me-3" color="primary"
                                v-if="message.message_type == feedbackMessageTypes.feedbackUser">
                                <v-icon size="18" color="white">{{ icons.mdiAccountOutline }}</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-avatar v-else size="28">
                                <v-img size="18" :src="tenant.logo.thumbnail"></v-img>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-subtitle>
                                    <template v-if="message.message_type == feedbackMessageTypes.feedbackUser">
                                        {{ $t('Sinä') }}:
                                    </template>
                                    <template v-else>
                                        {{ tenant.name }}:
                                    </template>
                                    {{ getDateTime(message.insert_date) }}
                                    <v-divider class="my-1"></v-divider>
                                </v-list-item-subtitle>
                                {{ message.text }}
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>

                    <template v-if="canReply">
                        <VForm ref="form" @submit.prevent="submitReply">
                            <VTextarea outlined :label="$t('Kirjoita viesti')" v-model="newMessage"
                                :hint="$t('Voit lähettää yhden viestin vastauksena')" persistent-hint
                                :rules="[required]">
                            </VTextarea>
                            <VBtn type="submit" class="mt-4" :loading="isSubmitting" color="primary">{{ $t('Lähetä') }}
                            </VBtn>
                        </VForm>
                    </template>
                    <v-alert outlined type="success" v-if="feedback.completed">
                        {{ $t('Palaute on käsitelty') }}
                    </v-alert>
                </v-card-text>
                <v-card-text v-else class="text-center">
                    <VProgressCircular indeterminate></VProgressCircular>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script>
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import { useTranslations } from "@/@core/libs/i18n";
import api from "@/api";
import useAuth from "@/useAuth";
import { getDateTime } from "@/@core/utils/datetime_helpers";
import { required } from "@/@core/utils/validation";
import feedbackMessageTypes from '@/@core/utils/feedback_message_types';
import FormSubmissionRenderer from "@/components/forms/FormSubmissionRenderer.vue";
import {
    mdiEmailOutline,
    mdiDotsVertical,
    mdiAccountOutline,
    mdiChatOutline,
    mdiReply,
    mdiCheckCircleOutline,
    mdiAttachment
} from "@mdi/js";
import { VProgressCircular } from "vuetify/lib";
import { VTextarea } from "vuetify/lib";
import { VIcon } from "vuetify/lib";
import { i18n } from "@/plugins/i18n";

export default {
    props: ["id"],
    setup(props) {

        const form = ref(null);
        const feedback = ref(null);
        const { t } = useTranslations();
        const { tenant } = useAuth();
        const newMessage = ref('');
        const isSubmitting = ref(false);

        const fetchFeedback = async (id) => {
            const res = await api.getMyFeedback(id);
            if (res.ok) {
                feedback.value = res.data;
            }
            else {
            }
        };

        const submitReply = async () => {
            if (form.value.validate() && confirm(i18n.t('Haluatko varmasti lähettää viestin?'))) {
                isSubmitting.value = true;
                const res = await api.saveMyFeedbackmessage(props.id, newMessage.value);
                if (res.ok) {
                    feedback.value.messages.push(res.data);
                }
                isSubmitting.value = false;
            }
        };

        onMounted(() => {
            if (props.id)
                fetchFeedback(props.id);
        });

        const canReply = computed(() => {
            return feedback.value?.messages?.length && feedback.value?.messages[feedback.value.messages.length - 1].message_type == feedbackMessageTypes.company &&
                !feedback.value.completed;
        });

        return {
            feedbackMessageTypes,
            getDateTime,
            tenant,
            feedback,
            canReply,
            required,
            form,
            submitReply,
            isSubmitting,
            newMessage,
            icons: {
                mdiEmailOutline,
                mdiChatOutline,
                mdiDotsVertical,
                mdiAccountOutline,
                mdiReply,
                mdiCheckCircleOutline,
                mdiAttachment
            },
        };
    },
    components: { VProgressCircular, VTextarea, VIcon, FormSubmissionRenderer }
};
</script>

<style></style>
